import { CoreApi, ProductType } from '@inter-ikea-storageone/range';
import { SelectedMenu, SelectedMenuProductList } from 'rangeConfig/@types';
import deleteSelectedEntity from 'rangeConfig/shared/behaviors/deleteSelectedEntity';
import replaceProduct from 'rangeConfig/shared/behaviors/replaceProduct';
import {
  matchDimensions,
  matchFrameFrontType
} from 'rangeConfig/shared/filters/selectedMenuProductFilters';
import platform from 'rangeConfig/shared/utils/platform';
import {
  hideChangingTableFunctionWidget,
  hideLoftBedOrientationWidget
} from 'rangeConfig/smastad/rules/hideMenuRules';
import hideDoors from 'rangeConfig/shared/behaviors/hideDoors';
import addToTarget from 'rangeConfig/shared/behaviors/addToTarget';
import {
  matchAgeGroup,
  matchDrawerToBench,
  matchFrameFrontSize,
  matchFrontLayoutToLoftBedDesk,
  matchInteriorSize,
  matchInteriorsToFramePlacement,
  matchInteriorSuggestionToLoftBedCabinet
} from 'rangeConfig/smastad/filters/selectedMenuProductFilters';
import addProduct from 'rangeConfig/shared/behaviors/addProduct';
import { noProductsWithTypesForSelectedEntity } from 'rangeConfig/shared/rules/noneOptionSelectedRules';
import removeBenchDrawer from 'rangeConfig/smastad/behaviors/removeBenchDrawer';
import removeAllInteriorsFromSelectedFrame from 'rangeConfig/shared/behaviors/removeAllInteriorsFromSelectedFrame';
import ChangeChangingTableDeskFunctionality from 'rangeConfig/smastad/widgets/ChangeChangingTableDeskFunctionality';
import DimensionsLabel from 'rangeConfig/shared/widgets/DimensionsLabel';
import removeAccessoriesForSelectedEntity from 'rangeConfig/smastad/behaviors/removeAccessoriesForSelectedEntity';
import addAndReplaceToTarget from 'rangeConfig/shared/behaviors/addAndReplaceToTarget';
import { isProductType } from 'rangeConfig/shared/rules/hideMenuRules';
import showDoors from 'rangeConfig/shared/behaviors/showDoors';
import addFrontStyleToFrame from 'rangeConfig/shared/behaviors/addFrontStyleToFrame';
import { getQPPImage } from 'products';
import ChangeLoftBedDeskOrientation from 'rangeConfig/smastad/widgets/ChangeLoftBedDeskOrientation';
import ChangeLoftBedCabinetOrientation from 'rangeConfig/smastad/widgets/ChangeLoftBedCabinetOrientation';
import addFrontLayoutToLoftBed from 'rangeConfig/smastad/behaviors/addFrontLayoutToLoftBed';
import addInteriorSuggestionToLoftBed from 'rangeConfig/smastad/behaviors/addInteriorSuggestionToLoftBed';
import {
  noInteriorsInLoftBedCabinet,
  noInteriorsInLoftBedDesk
} from 'rangeConfig/smastad/rules/selectedRules';
import removeInteriorsFromLoftBedCabinet from 'rangeConfig/smastad/behaviors/removeInteriorsInLoftBedCabinet';
import removeDrawersInLoftBedDesk from 'rangeConfig/smastad/behaviors/removeDrawersInLoftBedDesk';

const getMenuTitle = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const isLoftBed =
    selectedEntity && coreApi.entities.isProductType(selectedEntity, ProductType.SMASTAD_LOFT_BED);
  if (isLoftBed) {
    return 'storageone.smastadLoftBedSelectedMenuTitle';
  } else {
    return 'storageone.productListCustomiseCabinetsTitle';
  }
};

const benchDrawers = {
  id: 'bench-selected-menu-drawers',
  title: 'storageone.filterOptionDrawers',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText',
  productThumbToolTip: 'name, designText',
  behaviors: {
    productDropped: [addAndReplaceToTarget],
    productClicked: [addAndReplaceToTarget]
  },
  productTypes: [ProductType.SMASTAD_BENCH_DRAWER],
  filters: [],
  productFilters: [matchDrawerToBench],
  noneOption: {
    behaviors: { removeProducts: [removeBenchDrawer] },
    selected: noProductsWithTypesForSelectedEntity([ProductType.SMASTAD_BENCH_DRAWER]),
    title: 'storageone.productListNoDrawerOptionTitle'
  }
};

const benchPad = {
  id: 'bench-selected-menu-pad',
  title: 'storageone.selectedMenuBenchPadTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText',
  behaviors: {
    productDropped: [addAndReplaceToTarget],
    productClicked: [addAndReplaceToTarget]
  },
  productTypes: [ProductType.SMASTAD_BENCH_PAD],
  hidden: (coreApi: CoreApi, selectedEntityId: number) =>
    !isProductType(coreApi, selectedEntityId, ProductType.SMASTAD_BENCH),
  filters: [],
  productFilters: [],
  noneOption: {
    behaviors: { removeProducts: [removeAccessoriesForSelectedEntity] },
    selected: noProductsWithTypesForSelectedEntity([ProductType.SMASTAD_BENCH_PAD]),
    title: 'storageone.productListNoBenchPadOptionTitle'
  }
};

const interiors = {
  id: 'furniture-selected-menu-interiors',
  title: 'storageone.productListInteriorsTitle',
  cardsSubtitle: 'typeName, designText',
  productThumbToolTip: 'typeName, designText',
  behaviors: {
    productClicked: [hideDoors, addToTarget],
    productDropped: [hideDoors, addProduct]
  },
  productTypes: [
    ProductType.SMASTAD_SHELF,
    ProductType.SMASTAD_CLOTH_RAIL_40,
    ProductType.SMASTAD_CLOTH_RAIL_ASM,
    ProductType.SMASTAD_WIRE_BASKET_ASM,
    ProductType.SMASTAD_RAIL_STRIP_W_HOOKS_ASM,
    ProductType.SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM
  ],
  productFilters: [matchInteriorSize, matchInteriorsToFramePlacement],
  noneOption: {
    behaviors: { removeProducts: [removeAllInteriorsFromSelectedFrame] },
    selected: noProductsWithTypesForSelectedEntity([
      ProductType.SMASTAD_SHELF,
      ProductType.SMASTAD_CLOTH_RAIL_40,
      ProductType.SMASTAD_CLOTH_RAIL_ASM,
      ProductType.SMASTAD_WIRE_BASKET_ASM,
      ProductType.SMASTAD_RAIL_STRIP_W_HOOKS_ASM,
      ProductType.SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM
    ]),
    title: 'storageone.productListNoInteriorsOptionTitle'
  }
};

const frontStyle: SelectedMenuProductList = {
  id: 'furniture-selected-menu-front-style',
  title: 'storageone.frontStyleTabTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText',
  productCardMainImage: getQPPImage,
  productCardHoverImage: getQPPImage,
  cluster: ['name', 'design'],
  hidden: (coreApi: CoreApi, selectedEntityId: number) =>
    isProductType(coreApi, selectedEntityId, ProductType.SMASTAD_BENCH),
  filters: [],
  behaviors: {
    productDropped: [showDoors, addFrontStyleToFrame],
    productClicked: [showDoors, addFrontStyleToFrame]
  },
  productTypes: [ProductType.SMASTAD_HINGE_DOOR_ASM, ProductType.SMASTAD_DRAWER_ASM],
  productFilters: [matchFrameFrontType, matchFrameFrontSize],
  sortOrder: [{ path: 'communicatedDimensions.height', reverse: true }]
};

const loftBedFrontLayout: SelectedMenuProductList = {
  id: 'furniture-selected-menu-front-layout',
  title: 'storageone.smastadLoftBedDeskLayoutSelectedMenuTitle',
  cardsTitle: 'name',
  productThumbToolTip: 'name',
  cardsSubtitle: '',
  hidden: (coreApi: CoreApi, selectedEntityId: number) =>
    !isProductType(coreApi, selectedEntityId, ProductType.SMASTAD_LOFT_BED),
  filters: [],
  selected: () => false,
  behaviors: {
    productDropped: [showDoors, addFrontLayoutToLoftBed],
    productClicked: [showDoors, addFrontLayoutToLoftBed]
  },
  productTypes: [ProductType.SMASTAD_FRONT_LAYOUT],
  productFilters: [matchFrontLayoutToLoftBedDesk],
  sortOrder: [],
  noneOption: {
    behaviors: { removeProducts: [removeDrawersInLoftBedDesk] },
    selected: noInteriorsInLoftBedDesk,
    title: 'storageone.productListNoDrawerOptionTitle'
  }
};

const loftBedInteriorSuggestions: SelectedMenuProductList = {
  id: 'furniture-selected-menu-interior-suggestion',
  title: 'storageone.smastadLoftBedWardrobeLayoutSelectedMenuTitle',
  productThumbToolTip: 'name',
  cardsTitle: '',
  cardsSubtitle: '',
  hidden: (coreApi: CoreApi, selectedEntityId: number) =>
    !isProductType(coreApi, selectedEntityId, ProductType.SMASTAD_LOFT_BED),
  filters: [],
  selected: () => false,
  behaviors: {
    productDropped: [hideDoors, addInteriorSuggestionToLoftBed],
    productClicked: [hideDoors, addInteriorSuggestionToLoftBed]
  },
  productTypes: [ProductType.SMASTAD_INTERIOR_SUGGESTION],
  productFilters: [matchInteriorSuggestionToLoftBedCabinet, matchAgeGroup],
  sortOrder: [],
  noneOption: {
    behaviors: { removeProducts: [removeInteriorsFromLoftBedCabinet, hideDoors] },
    selected: noInteriorsInLoftBedCabinet,
    title: 'storageone.productListNoInteriorsOptionTitle'
  }
};

const widgets = {
  desktop: [
    {
      id: 'display-size',
      title: 'storageone.sizeFilterLabel',
      component: DimensionsLabel
    },
    {
      id: 'loft-bed-desk-orientation',
      title: 'storageone.smastadLoftBedOrientationDeskAndStairsTitle',
      component: ChangeLoftBedDeskOrientation,
      hidden: hideLoftBedOrientationWidget
    },
    {
      id: 'loft-bed-cabinet-orientation',
      title: 'storageone.smastadLoftBedOrientationWardrobeTitle',
      component: ChangeLoftBedCabinetOrientation,
      hidden: hideLoftBedOrientationWidget
    },
    {
      id: 'changing-table-desk-functionality',
      component: ChangeChangingTableDeskFunctionality,
      title: 'storageone.function',
      hidden: hideChangingTableFunctionWidget
    },
    frontStyle,
    loftBedFrontLayout,
    loftBedInteriorSuggestions,
    interiors,
    benchPad,
    benchDrawers
  ],
  mobile: [
    {
      id: 'furniture-selected-menu-doors',
      title: 'storageone.productListCabinetsTitle',
      cardsTitle: '',
      cardsSubtitle: '',
      productThumbToolTip: 'name',
      behaviors: {
        productClicked: [replaceProduct],
        productDropped: [replaceProduct]
      },
      noThumbs: () => true,
      productTypes: [ProductType.SMASTAD_FRAME],
      productFilters: [matchDimensions]
    },
    frontStyle,
    interiors,
    benchPad,
    benchDrawers,
    {
      id: 'loft-bed-desk-orientation',
      title: 'storageone.smastadLoftBedOrientationDeskAndStairsTitle',
      component: ChangeLoftBedDeskOrientation,
      hidden: hideLoftBedOrientationWidget
    },
    {
      id: 'loft-bed-cabinet-orientation',
      title: 'storageone.smastadLoftBedOrientationWardrobeTitle',
      component: ChangeLoftBedCabinetOrientation,
      hidden: hideLoftBedOrientationWidget
    },
    loftBedFrontLayout,
    loftBedInteriorSuggestions
  ]
};

const selectedMenu: SelectedMenu = {
  menuTitle: getMenuTitle,
  behaviors: {
    productDeleted: [deleteSelectedEntity]
  },
  content: widgets[platform]
};

export default selectedMenu;
